import type { ComponentPropsWithoutRef } from "react";
import type { Components } from "react-markdown";
import { Link } from "@tanstack/react-router";
import { BUTTON_BLOCK_LANG, parseButtonBlock } from "@/lib/blog/button-block";
import type { Root, Parent, RootContent } from "mdast";
import { visit } from "unist-util-visit";
import { YouTubeEmbed } from "@/components/media/YouTubeEmbed";
import { VimeoEmbed } from "@/components/media/VimeoEmbed";

type VideoInfo =
  | { provider: "youtube"; id: string }
  | { provider: "vimeo"; id: string }
  | null;

export function parseEmbedSrc(src?: string): VideoInfo {
  if (!src) return null;
  let url: URL;
  try {
    url = new URL(src, "https://example.invalid");
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, "").toLowerCase();

  if (host === "youtube.com" || host === "youtube-nocookie.com" || host === "youtu.be") {
    const id =
      host === "youtu.be"
        ? url.pathname.slice(1)
        : url.pathname.startsWith("/embed/")
          ? url.pathname.slice("/embed/".length)
          : (url.searchParams.get("v") ?? "");
    const clean = id.split("/")[0];
    return clean ? { provider: "youtube", id: clean } : null;
  }

  if (host === "player.vimeo.com" || host === "vimeo.com") {
    const m = url.pathname.match(/(\d+)/);
    return m ? { provider: "vimeo", id: m[1] } : null;
  }

  return null;
}

const IFRAME_RE = /<iframe\b[^>]*>[\s\S]*?<\/iframe>|<iframe\b[^>]*\/?>/i;
const SRC_RE = /\bsrc\s*=\s*["']([^"']+)["']/i;
const TITLE_RE = /\btitle\s*=\s*["']([^"']*)["']/i;

/**
 * Wandelt rohe <iframe>-Blöcke aus Outrank-Artikeln in echte Knoten um, die
 * unsere Consent-Player rendern. Alles andere rohe HTML bleibt (wie bisher)
 * unberücksichtigt – kein rehype-raw, damit kein Fremd-Markup durchrutscht.
 */
export function remarkVideoEmbeds() {
  return (tree: Root) => {
    visit(tree, "html", (node, index, parent: Parent | undefined) => {
      if (!parent || index === null || index === undefined) return;
      const raw = node.value ?? "";
      const match = raw.match(IFRAME_RE);
      if (!match) return;
      const tag = match[0];
      const src = tag.match(SRC_RE)?.[1];
      const info = parseEmbedSrc(src);
      if (!info) return;
      const title = tag.match(TITLE_RE)?.[1] || "Video";
      const replacement = {
        type: "paragraph",
        children: [],
        data: {
          hName: "iframe",
          hProperties: {
            src,
            title,
            "data-provider": info.provider,
            "data-video-id": info.id,
          },
        },
      } as unknown as RootContent;
      parent.children.splice(index, 1, replacement);
    });
  };
}

// Rendert erlaubte Video-Iframes über unsere Consent-Komponenten,
// alles andere wird verworfen (kein Rohtext, kein Fremd-Frame).
function Iframe(props: ComponentPropsWithoutRef<"iframe">) {
  const info = parseEmbedSrc(props.src);
  if (!info) return null;
  const title = props.title ?? "Video";
  return (
    <div className="not-prose my-8">
      {info.provider === "youtube" ? (
        <YouTubeEmbed videoId={info.id} title={title} />
      ) : (
        <VimeoEmbed videoId={info.id} title={title} />
      )}
    </div>
  );
}

/**
 * Button-Baustein: ```button mit „Beschriftung | Ziel".
 *
 * Auf der Beitragsseite entfällt ein unvollständiger Block ohne Lücke im Text,
 * im Editor erscheint stattdessen ein Hinweis.
 */
function buttonPre(showHint: boolean) {
  return function Pre(props: ComponentPropsWithoutRef<"pre">) {
    const child = Array.isArray(props.children) ? props.children[0] : props.children;
    const codeProps =
      child && typeof child === "object" && "props" in (child as object)
        ? ((child as { props: { className?: string; children?: unknown } }).props ?? {})
        : null;
    const isButton = (codeProps?.className ?? "").includes(`language-${BUTTON_BLOCK_LANG}`);
    if (!isButton) return <pre {...props} />;

    const raw = flatten(codeProps?.children);
    const block = parseButtonBlock(raw);
    if (!block) {
      if (!showHint) return null;
      return (
        <p className="not-prose my-6 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
          Button-Baustein unvollständig. Erwartet wird eine Zeile „Beschriftung | Ziel",
          das Ziel als interner Pfad (/kontakt) oder vollständige Adresse (https://…).
        </p>
      );
    }

    const className =
      "not-prose my-6 inline-flex h-11 items-center justify-center rounded-md bg-brand-button px-6 font-display text-sm font-extrabold text-brand-button-foreground no-underline shadow transition-opacity hover:opacity-90";

    return block.external ? (
      <a href={block.href} target="_blank" rel="noopener noreferrer" className={className}>
        {block.label}
      </a>
    ) : (
      <Link to={block.href} className={className}>
        {block.label}
      </Link>
    );
  };
}

function flatten(children: unknown): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(flatten).join("");
  if (children && typeof children === "object" && "props" in (children as object)) {
    return flatten((children as { props: { children?: unknown } }).props?.children);
  }
  return "";
}

export const blogMarkdownComponents: Components = {
  iframe: Iframe as Components["iframe"],
  pre: buttonPre(false) as Components["pre"],
};

/** Gleiche Bausteine, aber mit Hinweis bei unvollständigem Button-Block. */
export const editorMarkdownComponents: Components = {
  iframe: Iframe as Components["iframe"],
  pre: buttonPre(true) as Components["pre"],
};
